import React, { useEffect, useState } from 'react';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import ArReconTransLine from '../models/arReconTransLine';

interface IOpSelection {
    entryNumber: number;
    description: string;
    referenceNumber: string;
    adebit: number;
    adebitInc: number;
}

interface IReconDialogProps {
    dialogName: string;
    hideDialog: () => void;
    lines: Array<ArReconTransLine>;
    opSelection?: IOpSelection | null;
    onAddOverpayment: () => void;
    onClearOverpayment: () => void;
    onOk: (lines: Array<ArReconTransLine>) => void;
}

// Mirrors Default_Recon_Totals: seeds each row's debit_used/debit_left/debit_inc or
// credit_used/credit_left/credit_inc, depending on transaction type.
const defaultReconTotals = (lines: Array<ArReconTransLine>): Array<ArReconTransLine> =>
    lines.map(line => {
        const l = { ...line };
        if (![3, 4, 10, 11, 14, 15].includes(l.transactionType)) {
            l.debitUsed = l.debit ?? 0;
            l.debitLeft = (l.debit ?? 0) - l.debitUsed;
            l.debitInc = 0;
        } else if ([3, 4].includes(l.transactionType)) {
            l.debitLeft = null;
            l.debitUsed = null;
            l.debitInc = null;
            l.creditLeft = 0;
            l.creditUsed = l.credit;
            l.creditInc = 0;
        } else {
            l.creditUsed = l.credit ?? 0;
            l.creditLeft = (l.credit ?? 0) - l.creditUsed;
        }
        return l;
    });

interface IArtTotals {
    debit: number;
    debitLeft: number;
    debitUsed: number;
    debitInc: number;
    credit: number;
    creditLeft: number;
    creditUsed: number;
    creditInc: number;
    tranTotal: number;
    dtranTotal: number;
}

const emptyArtTotals: IArtTotals = {
    debit: 0, debitLeft: 0, debitUsed: 0, debitInc: 0,
    credit: 0, creditLeft: 0, creditUsed: 0, creditInc: 0,
    tranTotal: 0, dtranTotal: 0
};

// Mirrors Calc_Recon_Totals exactly, including its asymmetry: debit_used, credit_used,
// and credit_left get unconditionally reset to their fully-reconciled defaults on every
// pass (matching the source), while debit_left and debit_inc are never touched here -
// they only ever change via Default_Recon_Totals, a double-click, or a direct edit.
const calcReconTotals = (lines: Array<ArReconTransLine>, adebitInc: number): { lines: Array<ArReconTransLine>; art: IArtTotals } => {
    const art: IArtTotals = { ...emptyArtTotals, debitInc: adebitInc ?? 0 };

    const updated = lines.map(line => {
        const l = { ...line };

        if (![2, 10, 11, 14, 15].includes(l.transactionType)) {
            l.debitUsed = l.debit ?? 0;
        } else {
            l.creditUsed = l.credit ?? 0;
            l.creditLeft = (l.credit ?? 0) - l.creditUsed;
        }

        if ([3, 4].includes(l.transactionType)) {
            l.debitLeft = null;
            l.debitUsed = null;
            l.debitInc = null;
            l.creditLeft = 0;
            l.creditUsed = l.credit;
            l.creditInc = 0;
        } else if ([10, 11, 14, 15].includes(l.transactionType)) {
            l.debitLeft = null;
            l.debitUsed = null;
            l.debitInc = null;
        } else {
            l.creditLeft = null;
            l.creditUsed = null;
            l.creditInc = null;
        }

        art.debit += l.debit ?? 0;
        art.debitLeft += l.debitLeft ?? 0;
        art.debitUsed += l.debitUsed ?? 0;
        art.debitInc += l.debitInc ?? 0;
        art.credit += l.credit ?? 0;
        art.creditLeft += l.creditLeft ?? 0;
        art.creditUsed += l.creditUsed ?? 0;
        art.creditInc += l.creditInc ?? 0;

        return l;
    });

    art.tranTotal = art.debitUsed + art.debitInc - (art.creditUsed + art.debitLeft);
    art.dtranTotal = Math.abs(art.tranTotal);

    return { lines: updated, art };
};

const round2 = (value: number) => Math.round(value * 100) / 100;

type EditableField = 'debitUsed' | 'debitLeft' | 'debitInc' | 'creditUsed' | 'creditLeft';

const ReconDialog = (props: IReconDialogProps) => {
    const [lines, setLines] = useState<Array<ArReconTransLine>>([]);
    const [art, setArt] = useState<IArtTotals>(emptyArtTotals);

    const adebitInc = props.opSelection?.adebitInc ?? 0;

    useEffect(() => {
        const seeded = defaultReconTotals(props.lines || []);
        const { lines: recalced, art: totals } = calcReconTotals(seeded, adebitInc);
        setLines(recalced);
        setArt(totals);
    }, [props.lines, adebitInc]);

    const recalc = (nextLines: Array<ArReconTransLine>) => {
        const { lines: recalced, art: totals } = calcReconTotals(nextLines, adebitInc);
        setLines(recalced);
        setArt(totals);
    };

    const handleRowDoubleClick = (index: number) => {
        const updated = [...lines];
        const l = { ...updated[index] };

        if (![2, 10, 11, 14, 15].includes(l.transactionType)) {
            l.debitUsed = l.debit ?? 0;
            l.debitLeft = (l.debit ?? 0) - l.debitUsed;
        } else {
            l.creditUsed = l.credit ?? 0;
            l.creditLeft = (l.credit ?? 0) - l.creditUsed;
        }

        updated[index] = l;
        recalc(updated);
    };

    const handleFieldBlur = (index: number, field: EditableField, rawValue: string) => {
        let value = Number(rawValue);
        if (isNaN(value)) {
            return;
        }

        const updated = [...lines];
        const l = { ...updated[index] };

        if (field === 'debitUsed' && ![3, 4, 10, 11, 14, 15].includes(l.transactionType) && value > (l.debit ?? 0)) {
            value = l.debit ?? 0;
        }

        l[field] = value;
        updated[index] = l;
        recalc(updated);
    };

    const OKClicked = () => {
        if (round2(art.tranTotal) !== 0) {
            window.alert('The Over/Under Payment transaction total must be equal to $0.00 (Zero) before posting the transaction. Please review your totals.');
            recalc(lines);
            return;
        }
        props.onOk(lines);
    };

    const editableCell = (line: ArReconTransLine, index: number, field: EditableField) => {
        const value = line[field];
        if (value == null) {
            return null;
        }
        return (
            <input
                className="field"
                style={{ width: '100%' }}
                key={`${line.entryNumber}-${field}-${value}`}
                defaultValue={value.toFixed(2)}
                onBlur={(e) => handleFieldBlur(index, field, e.target.value)}
            />
        );
    };

    return (
        <Dialog title="Over/Under Payment Reconciliation" dialogName={props.dialogName} isExitButtonInvisible={true} bodyStyle={{ width: '65%' }}>
            <div className="bottom-table-container">
                <table>
                    <thead>
                    <tr>
                        <th>Transaction Type</th>
                        <th>Ref. No.</th>
                        <th>Debit Amount</th>
                        <th>Debit Reconciled</th>
                        <th>Under Paid Amount</th>
                        <th>Over Paid Amount</th>
                        <th style={{ width: '2rem', border: 'none', background: 'transparent' }} />
                        <th>Credit Amount</th>
                        <th>Credit Reconciled</th>
                        <th>Over Paid Amount</th>
                    </tr>
                    </thead>
                    <tbody>
                    {lines.map((line, index: number) => (
                        <tr key={line.entryNumber} onDoubleClick={() => handleRowDoubleClick(index)}>
                            <td>{line.description}</td>
                            <td>{line.referenceNumber}</td>
                            <td style={{ backgroundColor: line.debit != null ? '#33cc33' : undefined }}>
                                {line.debit != null ? line.debit.toFixed(2) : ''}
                            </td>
                            <td>{editableCell(line, index, 'debitUsed')}</td>
                            <td>{editableCell(line, index, 'debitLeft')}</td>
                            <td>{editableCell(line, index, 'debitInc')}</td>
                            <td style={{ border: 'none', background: 'transparent' }} />
                            <td style={{ backgroundColor: line.credit != null ? '#e64545' : undefined, color: line.credit != null ? '#ffffff' : undefined }}>
                                {line.credit != null ? line.credit.toFixed(2) : ''}
                            </td>
                            <td>{editableCell(line, index, 'creditUsed')}</td>
                            <td>{editableCell(line, index, 'creditLeft')}</td>
                        </tr>
                    ))}
                    </tbody>
                    <tfoot>
                    <tr>
                        <td><input className="field" style={{ width: '100%' }} readOnly={true} value={props.opSelection?.description ?? ''} /></td>
                        <td><input className="field" style={{ width: '100%' }} readOnly={true} value={props.opSelection?.referenceNumber ?? ''} /></td>
                        <td><input className="field" style={{ width: '100%' }} readOnly={true} value={props.opSelection?.adebit != null ? props.opSelection.adebit.toFixed(2) : ''} /></td>
                        <td />
                        <td />
                        <td><input className="field" style={{ width: '100%' }} readOnly={true} value={props.opSelection?.adebitInc != null ? props.opSelection.adebitInc.toFixed(2) : ''} /></td>
                        <td style={{ border: 'none', background: 'transparent' }} />
                        <td colSpan={3} style={{ textAlign: 'center' }}>
                            <Button className="blue-hover-button" style={{ marginRight: '0.5rem' }} text={'Add Overpayment'} onClick={() => props.onAddOverpayment()} />
                            <Button className="blue-hover-button" text={'Clear Overpayment'} onClick={() => props.onClearOverpayment()} />
                        </td>
                    </tr>
                    <tr>
                        <td colSpan={2} style={{ fontWeight: 'bold', textAlign: 'right' }}>Transaction Totals:</td>
                        <td style={{ backgroundColor: '#33cc33', textAlign: 'center' }}>{art.debit.toFixed(2)}</td>
                        <td style={{ backgroundColor: '#33cc33', textAlign: 'center' }}>{art.debitUsed.toFixed(2)}</td>
                        <td style={{ backgroundColor: '#33cc33', textAlign: 'center' }}>{art.debitLeft.toFixed(2)}</td>
                        <td style={{ backgroundColor: '#33cc33', textAlign: 'center' }}>{art.debitInc.toFixed(2)}</td>
                        <td style={{ border: 'none', background: 'transparent' }} />
                        <td style={{ backgroundColor: '#e64545', color: '#ffffff', textAlign: 'center' }}>{art.credit.toFixed(2)}</td>
                        <td style={{ backgroundColor: '#e64545', color: '#ffffff', textAlign: 'center' }}>{art.creditUsed.toFixed(2)}</td>
                        <td style={{ backgroundColor: '#e64545', color: '#ffffff', textAlign: 'center' }}>{art.creditLeft.toFixed(2)}</td>
                    </tr>
                    <tr>
                        <td colSpan={6} style={{ textAlign: 'right' }}>
                            <span style={{ whiteSpace: 'nowrap', fontWeight: 'bold', marginRight: '0.5rem' }}>Transaction Balance:</span>
                            <span
                                style={{
                                    display: 'inline-block',
                                    padding: '2px 8px',
                                    fontWeight: 'bold',
                                    backgroundColor: round2(art.tranTotal) === 0 ? '#43a047' : '#e57373',
                                    color: '#ffffff'
                                }}
                            >
                                {art.dtranTotal.toFixed(2)}
                            </span>
                        </td>
                        <td style={{ border: 'none', background: 'transparent' }} />
                        <td colSpan={3} style={{ textAlign: 'center' }}>Must Equal $0.00 to complete Transaction</td>
                    </tr>
                    </tfoot>
                </table>
            </div>

            <div className="form-row" style={{ marginTop: '2rem', justifyContent: 'center' }}>
                <Button
                    className="blue-hover-button"
                    style={{ width: '18%', marginRight: '1rem' }}
                    text={'Post Transaction'}
                    onClick={() => OKClicked()}
                />
                <Button
                    className="blue-hover-button"
                    style={{ width: '15%', marginRight: '1rem' }}
                    text={'Cancel'}
                    onClick={() => props.hideDialog()}
                />
            </div>

            <label style={{marginTop: '1rem', fontSize: '1.2rem', fontStyle: 'italic' }}>
                Note: Transaction Balance = Debit Reconciled + Debit Over Paid Amount - Credit Reconciled
            </label>
        </Dialog>
    );
};

export default ReconDialog;



// WEBPACK FOOTER //
// ./src/components/accounts-receivable/dialogs/ReconDialog.tsx